import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ui/confirm-dialog";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (value: string) => Promise<void> | void;
};

export default function ForgotPasswordModal({ open, onOpenChange, onSend }: Props) {
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [sentOpen, setSentOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    } else {
      setValue("");
      setSending(false);
    }
  }, [open]);

  const handleSend = async () => {
    if (!value) {
      toast.error("Please provide your email or badge number");
      return;
    }
    try {
      setSending(true);
      await onSend(value);
      // close the input dialog after send — then show the notice dialog
      onOpenChange(false);
      setSentOpen(true);
    } catch (e: any) {
      toast.error(e?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <div className="flex items-start gap-4 p-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-primary"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Reset your password
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your email address or badge number and we'll send instructions to reset your
              password.
            </p>

            <div className="mt-4">
              <Label className="mb-2">Email or badge number</Label>
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. someone@example.com or 12345"
              />
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="mr-2">
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending}>
                {sending ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
      <ConfirmDialog
        open={sentOpen}
        onOpenChange={setSentOpen}
        ContentIcon={AlertTriangle}
        contentIconBgClass="cdms-primary-soft"
        contentIconColorClass="text-primary"
        title="Password reset temporarily unavailable"
        description={
          "Password reset is currently under development. Please contact your system administrator for assistance resetting your password. This feature will be available soon."
        }
        confirmLabel="OK"
        cancelClassName="hidden"
        onConfirm={() => setSentOpen(false)}
      />
    </Dialog>
  );
}
